// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { zodResolver } from '@hookform/resolvers/zod';
import { useStepsData } from '@shared/ui-kit';
import { isDefined } from '@shared/utils';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm, type UseFormSetValue, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAccountWorkflowData } from '@shared/advisory/account-workflow/providers';
import { useSnackbar } from 'notistack';
import {
  getMinDocumentExpirationDate,
  personalDataSchema,
  type PersonalDataFormData,
} from '@shared/advisory/account-workflow/types';

const DEFAULT_COUNTRY = 'spain';

const createEmptyAddress = (
  country: string = DEFAULT_COUNTRY,
): PersonalDataFormData['postal_address'] => ({
  street_type: '',
  street_name: '',
  street_number: '',
  additional_address_info: '',
  province: '',
  postal_code: '',
  city: '',
  country,
});

const COMMON_STREET_TYPE_ORDER = [
  'street',
  'avenue',
  'square',
  'tree-lined-avenue',
  'main-avenue',
  'boulevard',
  'road',
  'path',
  'passage',
  'promenade',
  'ring-road',
  'cross-street',
  'place',
  'neighborhood',
  'housing-development',
  'extension',
  'roundabout',
  'rua',
  'slope',
  'downhill-street',
  'uphill-street',
  'ravine-road',
  'colony',
  'district',
  'village',
  'hamlet',
  'settlement',
  'city-block',
  'residential-area',
  'industrial-estate',
  'park',
  'market',
  'municipality',
  'complex',
  'building',
  'block',
  'chalet',
  'farmhouse',
  'housing-group',
  'apartment',
  'penthouse',
  'post-office-box',
] as const;

const sortStreetTypeOptions = (
  options: Array<{ value: string; label: string }>,
  locale: string,
): Array<{ value: string; label: string }> => {
  const priority = new Map(
    COMMON_STREET_TYPE_ORDER.map((value, index) => [value, index]),
  );

  return [...options].sort((a, b) => {
    const aPriority = priority.get(
      a.value as (typeof COMMON_STREET_TYPE_ORDER)[number],
    );
    const bPriority = priority.get(
      b.value as (typeof COMMON_STREET_TYPE_ORDER)[number],
    );

    if (isDefined(aPriority) && isDefined(bPriority)) {
      return aPriority - bPriority;
    }

    if (isDefined(aPriority)) {
      return -1;
    }

    if (isDefined(bPriority)) {
      return 1;
    }

    return a.label.localeCompare(b.label, locale);
  });
};

export const usePersonalData = () => {
  const {
    markStepAsCompleted,
    getFormData,
    savePersonalData,
    isUpdatingCitecUserClientProfile,
  } = useAccountWorkflowData();

  const { next } = useStepsData();

  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const savedData = getFormData('personalData');

  const form = useForm<PersonalDataFormData>({
    resolver: zodResolver(personalDataSchema),
    mode: 'onChange',
    defaultValues: savedData,
  });

  const hasDifferentFiscalAddress = useWatch({
    control: form.control,
    name: 'has_different_fiscal_address',
  });

  const hasPermanentValidity = useWatch({
    control: form.control,
    name: 'document_permanent_validity',
  });

  const postalCountry = useWatch({
    control: form.control,
    name: 'postal_address.country',
  });

  const fiscalCountry = useWatch({
    control: form.control,
    name: 'fiscal_address.country',
  });

  const minDocumentExpirationDate = useMemo(
    () => getMinDocumentExpirationDate(),
    [],
  );

  const documentTypeOptions = useMemo(() => {
    const items = t('document-types', {
      ns: 'inversis-document-types-f',
      returnObjects: true,
    }) as Record<string, string>;

    return Object.entries(items)
      .map(([value, label]) => ({
        value,
        label,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, i18n.language));
  }, [i18n.language, t]);

  const countryOptions = useMemo(() => {
    const items = t('countries', {
      ns: 'inversis-countries',
      returnObjects: true,
    }) as Record<string, string>;

    return Object.entries(items)
      .map(([value, label]) => ({
        value,
        label,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, i18n.language));
  }, [i18n.language, t]);

  const maritalStatusOptions = useMemo(
    () => [
      {
        value: 'single',
        label: t(
          'account-opening.personal-data.basic-info.marital-status.options.single',
          { ns: 'components' },
        ),
      },
      {
        value: 'married',
        label: t(
          'account-opening.personal-data.basic-info.marital-status.options.married',
          { ns: 'components' },
        ),
      },
    ],
    [t],
  );

  const genderOptions = useMemo(
    () => [
      {
        value: 'male',
        label: t('common.male', { ns: 'common' }),
      },
      {
        value: 'female',
        label: t('common.female', { ns: 'common' }),
      },
    ],
    [t],
  );

  const provinceOptions = useMemo(() => {
    const items = t('provinces-by-country', {
      ns: 'inversis-provinces',
      returnObjects: true,
    }) as Record<string, Record<string, string>>;

    return Object.entries(items)
      .flatMap(([countryId, provinces]) =>
        Object.entries(provinces).map(([value, label]) => ({
          value,
          label,
          countryId,
        })),
      )
      .sort((a, b) => a.label.localeCompare(b.label, i18n.language));
  }, [i18n.language, t]);

  const postalProvinceOptions = useMemo(() => {
    return provinceOptions.filter(item => item.countryId === postalCountry);
  }, [postalCountry, provinceOptions]);

  const fiscalProvinceOptions = useMemo(() => {
    return provinceOptions.filter(item => item.countryId === fiscalCountry);
  }, [fiscalCountry, provinceOptions]);

  const streetTypeOptions = useMemo(() => {
    const items = t('vias', { ns: 'inversis-vias', returnObjects: true }) as Record<
      string,
      string
    >;

    return sortStreetTypeOptions(
      Object.entries(items).map(([value, label]) => ({
        value,
        label,
      })),
      i18n.language,
    );
  }, [i18n.language, t]);

  const hasPostalProvinceOptions = useMemo(() => {
    return postalProvinceOptions.length > 0;
  }, [postalProvinceOptions]);

  const hasFiscalProvinceOptions = useMemo(() => {
    return fiscalProvinceOptions.length > 0;
  }, [fiscalProvinceOptions]);

  const clearFiscalAddressErrors = useCallback(() => {
    (
      [
        'fiscal_address',
        'fiscal_address.country',
        'fiscal_address.province',
        'fiscal_address.city',
        'fiscal_address.street_type',
        'fiscal_address.street_name',
        'fiscal_address.street_number',
        'fiscal_address.postal_code',
        'fiscal_address.additional_address_info',
      ] as const
    ).forEach(path => form.clearErrors(path));
  }, [form]);

  const setFieldSilently: UseFormSetValue<PersonalDataFormData> = useCallback(
    (path, value) => {
      const currentValue = form.getValues(path);

      if (currentValue === value) {
        return;
      }

      form.setValue(path, value, {
        shouldDirty: false,
        shouldValidate: false,
      });
    },
    [form],
  );

  const resetField: UseFormSetValue<PersonalDataFormData> = useCallback(
    (path, value) => {
      const currentValue = form.getValues(path);

      if (currentValue === value) {
        return;
      }

      form.setValue(path, value, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [form],
  );

  const isFormValid = useCallback(() => {
    return form.formState.isValid;
  }, [form.formState.isValid]);

  const onNext = useCallback(
    async (data: PersonalDataFormData) => {
      try {
        await savePersonalData(data);
        markStepAsCompleted('personal-data');
        next();
      } catch {
        enqueueSnackbar(
          t('personal-data.profile-save-error', { ns: 'components' }),
          {
            variant: 'error',
          },
        );
      }
    },
    [enqueueSnackbar, markStepAsCompleted, next, savePersonalData, t],
  );

  useEffect(() => {
    if (!postalCountry?.trim()) {
      resetField('postal_address.country', DEFAULT_COUNTRY);
    }
  }, [postalCountry, resetField]);

  useEffect(() => {
    if (!hasDifferentFiscalAddress) {
      setFieldSilently('fiscal_address', undefined);
      clearFiscalAddressErrors();
      return;
    }

    if (!form.getValues('fiscal_address')) {
      setFieldSilently('fiscal_address', createEmptyAddress());
      clearFiscalAddressErrors();
    }
  }, [clearFiscalAddressErrors, form, hasDifferentFiscalAddress, setFieldSilently]);

  useEffect(() => {
    if (hasDifferentFiscalAddress && !fiscalCountry?.trim()) {
      setFieldSilently('fiscal_address.country', DEFAULT_COUNTRY);
    }
  }, [fiscalCountry, hasDifferentFiscalAddress, setFieldSilently]);

  useEffect(() => {
    if (hasPermanentValidity) resetField('document_expiration_date', '');
  }, [hasPermanentValidity, resetField]);

  useEffect(() => {
    if (!hasPostalProvinceOptions) {
      resetField('postal_address.province', '');
      resetField('postal_address.street_type', '');
    }
  }, [hasPostalProvinceOptions, resetField]);

  useEffect(() => {
    if (hasDifferentFiscalAddress && !hasFiscalProvinceOptions) {
      setFieldSilently('fiscal_address.province', '');
      setFieldSilently('fiscal_address.street_type', '');
    }
  }, [hasDifferentFiscalAddress, hasFiscalProvinceOptions, setFieldSilently]);

  return {
    form,
    onNext,
    isFormValid,
    countryOptions,
    maritalStatusOptions,
    genderOptions,
    documentTypeOptions,
    postalProvinceOptions,
    fiscalProvinceOptions,
    streetTypeOptions,
    hasDifferentFiscalAddress,
    hasPermanentValidity,
    hasPostalProvinceOptions,
    hasFiscalProvinceOptions,
    minDocumentExpirationDate,
    t,
    isUpdatingProfile: isUpdatingCitecUserClientProfile,
  };
};
